import _ from 'underscore';
import stringify from 'json-stable-stringify';
import type {
  DataRefId, FetchPolicyValue, OverreactSpec, SpecType,
} from '../../public-types';
import type { Store } from '../../store';
import { getDataRef, getRecords } from '../../store';
import { responseTypes } from '../../spec/response-types';
import { specTypes } from '../../spec/spec-types';

export const DEFAULT_STORE_EXPIRATION_DURATION = 5 * 60 * 1000;

export const FetchPolicy = {
  StoreOnly: 'StoreOnly',
  NetworkOnly: 'NetworkOnly',
  StoreOrNetwork: 'StoreOrNetwork',
  NetworkOrStore: 'NetworkOrStore',
} as const;

export function IsNetworkPolicy(fetchPolicy?: FetchPolicyValue) {
  return fetchPolicy === FetchPolicy.NetworkOnly
    || fetchPolicy === FetchPolicy.NetworkOrStore
    || fetchPolicy === FetchPolicy.StoreOrNetwork;
}

export function IsStorePolicy(fetchPolicy?: FetchPolicyValue) {
  return fetchPolicy === FetchPolicy.StoreOnly
    || fetchPolicy === FetchPolicy.StoreOrNetwork
    || fetchPolicy === FetchPolicy.NetworkOrStore;
}

export function IsStoreSecondaryPolicy(fetchPolicy?: FetchPolicyValue) {
  return fetchPolicy === FetchPolicy.NetworkOrStore;
}

export function getFetchPolicy(
  specType: SpecType,
  fetchPolicyOption: FetchPolicyValue | undefined,
  fetchPolicyInEnvironment: FetchPolicyValue,
) {
  if (specType === specTypes.FETCH
    || specType === specTypes.PAGINATION
    || fetchPolicyOption !== FetchPolicy.StoreOnly) {
    return fetchPolicyOption || fetchPolicyInEnvironment;
  }
  return fetchPolicyInEnvironment;
}

export function getDataFromStore(
  store: Store,
  spec: OverreactSpec,
  dataRefId: DataRefId,
) {
  const records = getRecords(store, spec.requestContract, dataRefId) || [];
  const dataInStore = _.map(records, record => record?.getData()).filter(Boolean);
  if (spec.responseContract.responseType === responseTypes.ENTITY
    && !_.isEmpty(dataInStore) && _.isArray(dataInStore)) {
    return dataInStore[0];
  }
  return dataInStore;
}

interface ForceNetworkOptions<TVariables extends object> {
  store: Store;
  spec: OverreactSpec<TVariables>;
  dataRefId: DataRefId;
  variables: TVariables;
  storeExpirationDuration: number;
  currentTimestamp: number;
  requestFetchPolicy: FetchPolicyValue;
}

export function shouldForceNetwork<TVariables extends object>({
  store,
  spec,
  dataRefId,
  variables,
  storeExpirationDuration,
  currentTimestamp,
  requestFetchPolicy,
}: ForceNetworkOptions<TVariables>) {
  const dataRef = getDataRef(store, spec.requestContract, dataRefId);
  const { previousVariables, lastUpdateTimestamp = 0 } = dataRef?.status || {};
  return requestFetchPolicy !== FetchPolicy.StoreOnly
    && (stringify(variables) !== stringify(previousVariables)
      || currentTimestamp - lastUpdateTimestamp > storeExpirationDuration
      || spec.specType === specTypes.ADD
      || spec.specType === specTypes.DELETE
      || spec.specType === specTypes.MUTATION);
}

interface UpdateDataRefStatusOptions<TVariables extends object> {
  store: Store;
  spec: OverreactSpec<TVariables>;
  dataRefId: DataRefId;
  variables: TVariables;
  currentTimestamp: number;
}

export function updateDataRefStatus<TVariables extends object>({
  store,
  spec,
  dataRefId,
  variables,
  currentTimestamp,
}: UpdateDataRefStatusOptions<TVariables>) {
  getDataRef(store, spec.requestContract, dataRefId)?.updateStatus({
    previousVariables: variables,
    lastUpdateTimestamp: currentTimestamp,
  });
}
